import { useState } from "react";
import { Link } from "react-router-dom";
import { PageHero, PageLayout } from "../components/PageShell";
import { Placeholder } from "../components/Placeholder";
import { Figure } from "../components/Figure";
import { BOARD } from "../lib/board";
import { DEANS, deanPhoto } from "../lib/deans";
import { ROUTES } from "../lib/routes";
import { VISION, MISSION, QUALITY_POLICY } from "../lib/institute";
import { VISITS, VISIT_COUNT } from "../lib/visits";
import { MAIN_BLOCK } from "../lib/campusMedia";
import { BEA, BEA_HQ_PHOTO } from "../lib/bea";
import { AICTE_LOGO, NAAC_LOGO, NBA_LOGO, UGC_LOGO, VTU_LOGO } from "../lib/accreditationMedia";
import { useSectionTabs } from "../lib/useSectionTabs";
import { LetterDialog } from "../components/LetterDialog";
import { LEADERSHIP_MESSAGES } from "../lib/leadershipMessages";

/** Same set, in the same order, as the home page's Accreditations strip. */
const ACCREDITATIONS = [
  { logo: NAAC_LOGO, name: "NAAC", sub: "‘A’ grade" },
  { logo: NBA_LOGO, name: "NBA", sub: "UG programmes" },
  { logo: AICTE_LOGO, name: "AICTE", sub: "Approved" },
  { logo: VTU_LOGO, name: "VTU", sub: "Affiliated" },
  { logo: UGC_LOGO, name: "UGC", sub: "2(f) & 12(B)" },
  { mark: "AUTO", name: "Autonomous", sub: "Since 2024" },
];

const milestones = [
  { year: "1958", title: "Bapuji Educational Association founded", body: "Established in Davanagere with a mission to make quality education accessible to students across central Karnataka." },
  { year: "1979", title: "BIET established", body: "Bapuji Institute of Engineering & Technology begins with three undergraduate engineering branches and 180 students." },
  { year: "1996", title: "VTU affiliation", body: "Affiliated to the newly formed Visvesvaraya Technological University, Belagavi. PG programs introduced." },
  { year: "2007", title: "Research centers recognized", body: "First VTU-recognized research centers established in CSE, ECE and Mechanical Engineering." },
  { year: "2014", title: "NAAC and NBA accreditation", body: "Accredited 'A' by NAAC; UG programmes earn NBA accreditation; recognized by UGC under 2(f) and 12(B)." },
  { year: "2024", title: "Autonomous status conferred", body: "BIET Autonomous Status Launch & Student Induction Program — a milestone for curriculum independence and academic flexibility." },
  { year: "2026", title: "Today", body: "13 UG branches · 6 PG programmes · 15 VTU-recognised research centres · NAAC ‘A’ · NBA accredited · Autonomous." },
];

export default function About() {
  // Scroll-spy: highlight the "On this page" nav item for the section in view.
  const activeId = useSectionTabs(["bea", "vision", "accreditations", "milestones", "leadership", "leader-message", "deans", "visitors"], "bea");
  const [activeLeader, setActiveLeader] = useState(0);
  const [leaderLetterOpen, setLeaderLetterOpen] = useState(false);
  const leaderSlide = LEADERSHIP_MESSAGES[activeLeader];
  return (
    <>
      <PageHero
        variant="hero-navy"
        eyebrow="About BIET"
        title="A Karnataka institution. Engineering since 1979."
        lead="Bapuji Institute of Engineering and Technology is a unit of Bapuji Educational Association — a charitable trust running 60+ institutions across Karnataka since 1958. We educate engineers for industry, research and entrepreneurship."
        crumbs={[{ label: "About" }]}
        heroMedia={MAIN_BLOCK}
        image="MAIN BLOCK · ARCHITECTURAL"
      />

      <div className="about-detail page-tabs">
      <PageLayout
        side={[
          {
            title: "On this page",
            items: [
              { label: "Bapuji Educational Association", to: "#bea", active: activeId === "bea" },
              { label: "Vision & mission", to: "#vision", active: activeId === "vision" },
              { label: "Accreditations", to: "#accreditations", active: activeId === "accreditations" },
              { label: "Milestones", to: "#milestones", active: activeId === "milestones" },
              { label: "Leadership", to: "#leadership", active: activeId === "leadership" },
              { label: "Leader's Message", to: "#leader-message", active: activeId === "leader-message" },
              { label: "Deans", to: "#deans", active: activeId === "deans" },
              { label: "Distinguished visitors", to: "#visitors", active: activeId === "visitors" },
            ],
          },
          {
            title: "Explore",
            items: [
              { label: "Academics", to: ROUTES.academics },
              { label: "Admissions", to: ROUTES.admissions },
              { label: "Mandatory Disclosures", to: ROUTES.disclosures },
              { label: "Contact", to: ROUTES.contact },
            ],
          },
        ]}
      >
        <h2 id="bea">Bapuji Educational Association</h2>
        <p>
          Empowering generations through {BEA.institutions} institutions in education since {BEA.founded}.
        </p>
        <div className="about-bea">
          <div>
            <p>
              With a legacy spanning over six decades, Bapuji Educational Association stands as a
              beacon of holistic education in Karnataka. It runs more than 60 institutions — nursery
              through postgraduate, spanning engineering, medicine, dentistry, pharmacy and nursing.
            </p>
            <p>
              That breadth is what earned Davanagere its name as the Oxford of Karnataka. BIET has
              been the association's engineering college since 1979.
            </p>
            <a className="btn-link" href="https://bapujidvg.in/" target="_blank" rel="noopener noreferrer">
              Read more <span className="arr">→</span>
            </a>
          </div>
          <Figure
            media={BEA_HQ_PHOTO}
            fallbackLabel="BEA HEADQUARTERS · DAVANAGERE"
            aspect="4/3"
            sizes="(max-width: 900px) 92vw, 380px"
          />
        </div>

        <h2 id="vision">Vision &amp; mission</h2>
        <p>The institute's charter, as it publishes it.</p>
        <div className="institute-charter">
          <div className="ic-statements">
            <figure className="ic-panel ic-panel--vision">
              <div className="kicker">Our vision</div>
              <blockquote className="ic-text">{VISION}</blockquote>
            </figure>
            <figure className="ic-panel ic-panel--mission">
              <div className="kicker">Our mission</div>
              <ol className="ic-mission-list">
                {MISSION.map((m, i) => (
                  <li key={i}>
                    <span className="n">{String(i + 1).padStart(2, "0")}</span>
                    <span className="t">{m}</span>
                  </li>
                ))}
              </ol>
            </figure>
          </div>
          <div className="ic-policy">
            <h3 className="ic-policy-head">Quality policy</h3>
            <ol className="ic-policy-grid">
              {QUALITY_POLICY.map((t, i) => (
                <li key={i}>
                  <span className="n">{String(i + 1).padStart(2, "0")}</span>
                  <span className="t">{t}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <h2 id="accreditations">Accreditations</h2>
        <p>Recognition that anchors every programme — accredited, affiliated and autonomous.</p>
        <div className="accred-row">
          {ACCREDITATIONS.map((a) => (
            <div key={a.name} className="accred-item">
              <div className={"accred-mark" + (a.logo ? " accred-mark--logo" : "")}>
                {a.logo ? (
                  <Figure
                    media={a.logo}
                    fallbackLabel={a.name}
                    aspect="1/1"
                    className="accred-logo"
                    sizes="34px"
                  />
                ) : (
                  a.mark
                )}
              </div>
              <div className="accred-text">
                <div className="accred-name">{a.name}</div>
                <div className="accred-sub">{a.sub}</div>
              </div>
            </div>
          ))}
        </div>

        <h2 id="milestones">Milestones</h2>
        <p style={{ maxWidth: "62ch" }}>Forty-six years told as a single line — the moments that shaped the institution.</p>
        <div className="timeline-scrubber reveal">
          <div
            className="timeline-scrubber-track"
            ref={(el: HTMLDivElement | null) => {
              if (!el || (el as any).dataset._wired) return;
              (el as any).dataset._wired = "1";
              const events = el.querySelectorAll(".timeline-event") as NodeListOf<HTMLElement>;
              const update = () => {
                const r = el.getBoundingClientRect();
                const center = r.left + r.width * 0.32;
                let best: HTMLElement | null = null;
                let bestDist = Infinity;
                events.forEach((ev) => {
                  const er = ev.getBoundingClientRect();
                  const c = er.left + er.width / 2;
                  const d = Math.abs(c - center);
                  if (d < bestDist) { bestDist = d; best = ev; }
                });
                events.forEach((ev) => ev.classList.toggle("is-active", ev === best));
              };
              el.addEventListener("scroll", update);
              window.addEventListener("resize", update);
              setTimeout(update, 100);
            }}
          >
            {milestones.map((m, i) => (
              <div key={i} className={"timeline-event " + (i === 0 ? "is-active" : "")}>
                <div className="timeline-event-year">{m.year}</div>
                <div className="timeline-event-tick"></div>
                <h4>{m.title}</h4>
                <p>{m.body}</p>
              </div>
            ))}
          </div>
        </div>

        <h2 id="leadership">Leadership & administration</h2>
        <p>The Governing Body of the Bapuji Educational Association — the charitable trust that founded and runs BIET.</p>
        <div className="leaders-grid">
          {BOARD.map((m) => (
            <div key={m.lNo} className="leader-card reveal">
              {/* 3/4 rather than a square: the source portraits are ~0.78,
                  so a 1/1 box cover-cropped roughly a fifth off the height
                  and clipped the tops of heads. */}
              <Figure media={m.photo} fallbackLabel="PORTRAIT" aspect="3/4" sizes="(max-width: 900px) 45vw, 220px" />
              <div className="leader-body">
                <div className="leader-name">{m.name}</div>
                <div className="leader-role">{m.role}</div>
              </div>
            </div>
          ))}
        </div>

        <h2 id="leader-message">Leader&rsquo;s Message</h2>
        <p>Notes from the Chairman, Principal and Director — one letter at a time.</p>
        <div className="leader-tabs" role="tablist" aria-label="Leader's message">
          {LEADERSHIP_MESSAGES.map((s, i) => (
            <button
              key={s.id}
              role="tab"
              aria-selected={i === activeLeader}
              aria-controls={`leader-message-panel-${s.id}`}
              className={"leader-tab" + (i === activeLeader ? " is-active" : "")}
              onClick={() => setActiveLeader(i)}
            >
              {s.role.split(" · ")[0]}
            </button>
          ))}
        </div>
        <div
          key={leaderSlide.id}
          id={`leader-message-panel-${leaderSlide.id}`}
          role="tabpanel"
          className={`director-letter leader-letter leader-letter--${leaderSlide.id} reveal`}
        >
          <div className="director-letter-grid">
            <div className="director-portrait">
              <Figure media={leaderSlide.photo} fallbackLabel={leaderSlide.photoLabel} aspect="4/5" />
              <div className="director-portrait-cap">{leaderSlide.name}<br /><span>{leaderSlide.role}</span></div>
            </div>
            <div className="director-letter-body">
              <div className="kicker">{leaderSlide.kicker}</div>
              <h3 style={{ marginTop: 14, marginBottom: 24 }}>{leaderSlide.quote}</h3>
              {leaderSlide.body.map((t, i) => <p key={i}>{t}</p>)}
              {leaderSlide.full && (
                <button
                  type="button"
                  className="letter-more"
                  onClick={() => setLeaderLetterOpen(true)}
                >
                  Read the full message <span aria-hidden>&rarr;</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <h2 id="deans">Deans</h2>
        <p>The deans who lead academics, research and student affairs across the institute.</p>
        <div className="leaders-grid">
          {DEANS.map((d) => (
            <div key={d.dNo} className="leader-card reveal">
              <Figure media={deanPhoto(d)} fallbackLabel="PORTRAIT" aspect="3/4" sizes="(max-width: 900px) 45vw, 220px" />
              <div className="leader-body">
                <div className="leader-name">{d.name}</div>
                <div className="leader-role">{d.role}</div>
              </div>
            </div>
          ))}
        </div>

        <h2 id="visitors">Distinguished visitors</h2>
        <p style={{ maxWidth: "62ch" }}>
          Scientists, academicians and public figures the institute has received — {VISIT_COUNT} guests
          in its own record, from a President of India to the teams that build the country's
          satellites.
        </p>
        <div className="visit-teaser reveal">
          {VISITS.slice(0, 5).map((v) => (
            <Link key={v.slug} to={ROUTES.visitors} className="visit-teaser-tile">
              <Figure media={v.photos[0]} fallbackLabel={v.name} aspect="4/5" sizes="150px" />
              <div className="visit-teaser-name">{v.name}</div>
              <div className="visit-teaser-honour">{v.honour ?? v.affiliation}</div>
            </Link>
          ))}
        </div>
        <Link className="btn-link" to={ROUTES.visitors}>
          All {VISIT_COUNT} visitors <span className="arr">→</span>
        </Link>
      </PageLayout>
      </div>

      {leaderSlide.full && (
        <LetterDialog
          open={leaderLetterOpen}
          onClose={() => setLeaderLetterOpen(false)}
          kicker={leaderSlide.kicker}
          headline={leaderSlide.quote}
          paragraphs={leaderSlide.full}
          name={leaderSlide.name}
          role={leaderSlide.role}
          photo={leaderSlide.photo}
          photoLabel={leaderSlide.photoLabel}
        />
      )}
    </>
  );
}
